import {MemberAvatar} from '@/components/styled/MemberAvatar';
import {useSidebar} from '@/contexts/SidebarContext/SidebarContext';
import {useUserTypeContext} from '@/contexts/UserTypeContext/UserTypeContext';
import {useUnreadNotificationsCountQuery} from '@/generated/graphql';
import {useSubscribeToNotifications} from '@/hooks/useInitializeNotifications';
import {ProfileMenuContent} from '@/layout/Page/ProfileMenuContent';
import {ConciergeSidebarContent} from '@/routes/Concierge/ConciergeSidebarContent';
import {ManagerSidebarContent} from '@/routes/Manager/ManagerSidebarContent';
import {userProfileContext} from '@ark/web';
import {
  KeyboardArrowUp,
  Notifications,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  IconButton,
  Menu,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useBrand} from '@/contexts/BrandContext';
import {useTheme} from '@mui/material/styles';
import {MouseEvent, useContext, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from '../sidebarWidths';

export const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {name: brandName, assets} = useBrand();
  const {isSidebarPinned, toggleSidebarPin} = useSidebar();
  const {firstName, lastName, email, displayPicture, loading} =
    useContext(userProfileContext);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const {concierge, manager} = useUserTypeContext();
  const isConciergeOnly = Boolean(concierge) && !manager;

  const sidebarWidth = isSidebarPinned
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const {data, refetch} = useUnreadNotificationsCountQuery();

  useSubscribeToNotifications(() => {
    refetch();
  });

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      //@ts-ignore
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    //@ts-ignore
    link.href =
      (data?.notifications?.totalCount || 0) > 0
        ? assets.faviconNotification
        : assets.favicon;
  }, [data, assets]);

  const unreadCount = data?.notifications?.totalCount ?? 0;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        width: `${sidebarWidth}px`,
        padding: isSidebarPinned ? '32px 11px' : '32px 14px',
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[100],
        height: '100vh',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: isSidebarPinned ? 'stretch' : 'center',
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSidebarPinned ? 'flex-start' : 'center',
          gap: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSidebarPinned ? 'space-between' : 'center',
            width: '100%',
            mb: 2,
            px: isSidebarPinned ? 1.5 : 0,
          }}
        >
          <Box
            onClick={() => navigate('/')}
            data-attr="sidebar-home-open"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <img
              src={assets.symbol}
              alt={brandName}
              style={{
                width: 26,
                height: 26,
                flexShrink: 0,
              }}
            />
            {isSidebarPinned && (
              <Box
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  letterSpacing: '-0.5px',
                }}
              >
                {brandName}
              </Box>
            )}
          </Box>
          {isSidebarPinned && (
            <Tooltip title="Unpin sidebar" placement="left">
              <IconButton
                onClick={toggleSidebarPin}
                data-attr="sidebar-pin-toggle"
                size="small"
                sx={{
                  p: 0.5,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                aria-label="Unpin sidebar"
              >
                <PushPin sx={{fontSize: 16}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {isConciergeOnly && (
            <ConciergeSidebarContent isPinned={isSidebarPinned} />
          )}
          {!isConciergeOnly && (
            <ManagerSidebarContent isPinned={isSidebarPinned} />
          )}
        </Box>
      </Box>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSidebarPinned ? 'stretch' : 'center',
          gap: isSidebarPinned ? '4px' : 1.5,
          width: '100%',
        }}
      >
        {!isSidebarPinned && (
          <Tooltip title="Pin sidebar" placement="right">
            <IconButton
              onClick={toggleSidebarPin}
              data-attr="sidebar-pin-toggle"
              size="small"
              sx={{
                p: 0.5,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              aria-label="Pin sidebar"
            >
              <PushPinOutlined sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
        )}
        {!isSidebarPinned ? (
          <Tooltip title="Notifications" placement="right">
            <Badge
              badgeContent={unreadCount}
              color="error"
              overlap="circular"
              max={99}
              anchorOrigin={{vertical: 'top', horizontal: 'left'}}
            >
              <IconButton
                onClick={() => navigate('/notifications')}
                data-attr="sidebar-notifications-open"
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  p: 0.5,
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <Notifications
                  sx={{
                    fontSize: 18,
                    color: theme.palette.text.secondary,
                  }}
                />
              </IconButton>
            </Badge>
          </Tooltip>
        ) : (
          <Box
            onClick={() => navigate('/notifications')}
            data-attr="sidebar-notifications-open"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                '& svg': {
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                overlap="circular"
                max={99}
                anchorOrigin={{vertical: 'top', horizontal: 'left'}}
              >
                <IconButton
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    p: 0.5,
                    borderRadius: '6px',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Notifications
                    sx={{
                      fontSize: 18,
                      color: theme.palette.text.secondary,
                    }}
                  />
                </IconButton>
              </Badge>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 1.2,
                letterSpacing: '0.1px',
                color: theme.palette.text.secondary,
              }}
            >
              Notifications
            </Typography>
          </Box>
        )}
        {!isSidebarPinned ? (
          <Box>
            <Tooltip title="Profile menu" placement="right">
              <IconButton
                onClick={handleOpenUserMenu}
                data-attr="sidebar-profile-menu-open"
                sx={{
                  p: 0,
                  width: 36,
                  height: 36,
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {loading && (
                  <Skeleton variant="circular" width={32} height={32} />
                )}
                {!loading && (
                  <MemberAvatar
                    displayPicture={displayPicture ?? undefined}
                    name={`${firstName} ${lastName}`}
                    size={32}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              sx={{mt: '8px', ml: '14px'}}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginTop: 1,
                  },
                },
              }}
            >
              <ProfileMenuContent onSelect={handleCloseUserMenu} />
            </Menu>
          </Box>
        ) : (
          <Box>
            <Tooltip
              title={
                firstName && lastName ? `${firstName} ${lastName}` : 'User'
              }
              placement="right"
              disableHoverListener={
                `${firstName ?? ''} ${lastName ?? ''}`.trim().length <= 15
              }
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'white',
                    color: theme.palette.text.primary,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    fontSize: 12,
                  },
                },
              }}
            >
              <Box
                onClick={handleOpenUserMenu}
                data-attr="sidebar-profile-menu-open"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {loading && (
                  <Skeleton variant="circular" width={28} height={28} />
                )}
                {!loading && (
                  <MemberAvatar
                    displayPicture={displayPicture ?? undefined}
                    name={`${firstName} ${lastName}`}
                    size={28}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    overflow: 'hidden',
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: 1.2,
                      letterSpacing: '0.1px',
                      color: theme.palette.text.primary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {firstName && lastName
                      ? `${firstName} ${lastName}`
                      : 'User'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 11,
                      fontWeight: 400,
                      lineHeight: 1.2,
                      letterSpacing: '0.1px',
                      color: theme.palette.text.secondary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {email || 'user@example.com'}
                  </Typography>
                </Box>
                <KeyboardArrowUp
                  sx={{
                    fontSize: 14,
                    color: theme.palette.text.secondary,
                    flexShrink: 0,
                    mr: -0.5,
                  }}
                />
              </Box>
            </Tooltip>
            <Menu
              sx={{mt: '8px', ml: '14px'}}
              id="menu-appbar-pinned"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    marginTop: 1,
                  },
                },
              }}
            >
              <ProfileMenuContent onSelect={handleCloseUserMenu} />
            </Menu>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
